import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, App as AntdApp, Button, Grid, Tag } from 'antd';

import ContextBar from '../components/common/ContextBar';
import RecordBadge from '../components/common/RecordBadge';
import { confirmDestructive } from '../components/common/confirmDestructive';
import MarkdownContent from '../components/common/MarkdownContent';
import EditListingModal, { keywordsOf } from '../components/publishing/EditListingModal';
import PackageVersions from '../components/publishing/PackageVersions';
import RebuildPackageModal from '../components/publishing/RebuildPackageModal';
import { requirementReportLines } from '../components/packages/RequirementFields';
import { STATUS_LABELS, VERSION_CHANGE_WORDS, buildReportLines, builtWords, packageIdentity, packageMetaLine, statusNote } from '../components/publishing/publishingWords';
import { DataState, Skeleton, deriveDataState } from '../components/loading';
import { PACKAGE_KIND_LABELS, packageContentsLine } from '../services/packages';
import {
	HELD_WORDS,
	deletePublisherPackage,
	getPublisherPackage,
	isHeld,
	publishPublisherPackage,
	publishingRefusal,
	retirePublisherPackage,
} from '../services/publishing';
import { packagePath } from '../utils/packageAddress';
import { apiErrorMessage } from '../utils/apiErrors';
import { formatDisplayDateTime } from '../utils/dateDisplay';
import { normalizeId } from '../utils/ids';

const PUBLISHING_CRUMB = { label: 'Publishing', to: '/publishing' };

/**
 * /publishing/packages/:packageId — one of a publisher's own packages
 * (keepr-api KPR-1331). The listing as Discover shows it and what is inside,
 * with the package's acts in the header: **Publish** (the one primary, while
 * it is not listed), **Edit listing…** and **Rebuild…**, and in the ⋯ Go to
 * (Open on Discover, Open its collection) and Danger (Retire…, then — for a
 * package never listed, keepr-api KPR-1445 — Delete draft…, last; both red
 * and confirmed). On a phone the two secondaries fold into the ⋯.
 *
 * Every version that would change what Discover lists is sent for review: the
 * API answers 202 and the page says {@link HELD_WORDS}. Every act sends the
 * `rev` the page read, so a change another of the publisher's accounts made
 * since is refused (409) and the page reads the package again.
 *
 * Its versions (keepr-api KPR-1327): the header says when one waits for
 * review (the version, what it changes, when it was sent), and the Versions
 * section after "What's inside" lists every one — listed, approved, not
 * approved with the reviewer's reason, withdrawn, replaced — with Withdraw…
 * on the caller's own still waiting (PackageVersions).
 */
/** "Waiting for review: version 2, new content, sent 27 Oct 2026, 10:00." */
function waitingWords(waiting) {
	const what = (VERSION_CHANGE_WORDS[waiting.change] || '').toLowerCase();
	const when = waiting.submittedAt ? `, sent ${formatDisplayDateTime(waiting.submittedAt)}` : '';
	const who = waiting.mine ? ' by you' : '';
	return `Waiting for review: version ${waiting.version || 1}${what ? `, ${what}` : ''}${when}${who}. Discover keeps showing what it lists now until a reviewer approves it.`;
}

export default function PublishingPackagePage() {
	const { packageId: rawId } = useParams();
	const packageId = normalizeId(rawId) || rawId;
	const navigate = useNavigate();
	const { modal, message } = AntdApp.useApp();
	const screens = Grid.useBreakpoint();
	const phone = screens.md === false;
	const [pkg, setPkg] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [missing, setMissing] = useState(null); // the words for a 404 or a 403
	const [notice, setNotice] = useState(null); // { type, text }
	const [busy, setBusy] = useState(null); // 'publish' | 'retire' | 'delete'
	const [editing, setEditing] = useState(false);
	const [rebuilding, setRebuilding] = useState(false);
	// Bumped on every read of the package, so its versions are read again with it.
	const [reads, setReads] = useState(0);
	const requestRef = useRef(0);

	const load = useCallback(() => {
		const request = ++requestRef.current;
		setLoading(true);
		setError(null);
		getPublisherPackage(packageId)
			.then((row) => { if (request === requestRef.current) { setPkg(row); setMissing(null); setLoading(false); setReads(n => n + 1); } })
			.catch((e) => {
				if (request !== requestRef.current) return;
				const status = e?.response?.status;
				if (status === 404) setMissing('This package is not one of your publisher\'s.');
				else if (status === 403) setMissing(e.response.data?.message || 'Publishing is for the accounts a publisher names.');
				else setError(e);
				setLoading(false);
			});
	}, [packageId]);

	useEffect(() => {
		load();
		return () => { requestRef.current += 1; };
	}, [load]);

	/** What an act answered: held for review, or done — then the package as it is now. */
	const settle = (answer, doneWords) => {
		if (isHeld(answer)) setNotice({ type: 'info', text: HELD_WORDS });
		else setNotice(doneWords ? { type: 'success', text: doneWords } : null);
		load();
	};

	const refusal = (e, fallback) => {
		const words = publishingRefusal(e) || apiErrorMessage(e, fallback);
		setNotice({ type: 'error', text: words });
		// A page another account changed since: read it again.
		if (e?.response?.data?.code === 'package_changed') load();
	};

	const act = async (verb, run, { done, fallback }) => {
		setBusy(verb);
		try {
			settle(await run(), done);
		} catch (e) {
			refusal(e, fallback);
		} finally {
			setBusy(null);
		}
	};

	const publish = () => modal.confirm({
		title: `Send “${pkg.name}” for review?`,
		content: 'A reviewer reads it against keepr\'s pack standards. Once one approves it, every signed-in account sees it on Discover and can add it.',
		okText: 'Send for review',
		onOk: () => act('publish', () => publishPublisherPackage(pkg._id, pkg.rev), { done: `Published “${pkg.name}”.`, fallback: 'The package could not be published.' }),
	});

	const rebuild = () => setRebuilding(true);

	const onRebuilt = (answer) => {
		setRebuilding(false);
		settle(answer, builtWords(pkg.name, null, { verb: 'Rebuilt', as: `as version ${(pkg.version || 1) + 1}` }));
	};

	const retire = () => confirmDestructive(modal, {
		title: `Retire “${pkg.name}”?`,
		content: 'It leaves Discover at once. Collections that already added it keep their copies. A version waiting for review is withdrawn.',
		okText: 'Retire',
		onOk: () => act('retire', () => retirePublisherPackage(pkg._id, pkg.rev), { done: `Retired “${pkg.name}”.`, fallback: 'The package could not be retired.' }),
	});

	// A package never listed (keepr-api KPR-1445): gone for good, then back to the list.
	const deleteDraft = () => confirmDestructive(modal, {
		title: `Delete “${pkg.name}”?`,
		content: 'It was never listed, so no collection added it. It is deleted for good, with its versions\' content; a version waiting for review is withdrawn. This cannot be undone.',
		okText: 'Delete draft',
		onOk: async () => {
			setBusy('delete');
			try {
				await deletePublisherPackage(pkg._id, pkg.rev);
				message.success(`Deleted “${pkg.name}”.`);
				navigate('/publishing');
			} catch (e) {
				refusal(e, 'The package could not be deleted.');
			} finally {
				setBusy(null);
			}
		},
	});

	const onListingSaved = (answer) => {
		setEditing(false);
		settle(answer, 'Listing saved.');
	};

	const listed = pkg?.status === 'published';
	const secondaries = pkg ? [
		{ key: 'edit', group: 'record', glyph: 'edit', label: 'Edit listing…', onClick: () => setEditing(true) },
		// Only someone who manages the collection it was built from (the API's `canRebuild`); the reason is said under What's inside.
		{ key: 'rebuild', group: 'record', label: 'Rebuild…', disabled: !pkg.canRebuild, onClick: rebuild },
	] : [];
	const menuActions = pkg ? [
		listed ? { key: 'open', group: 'navigate', glyph: 'open', label: 'Open on Discover', onClick: () => navigate(packagePath(pkg.key)) } : null,
		pkg.sourceCollectionId ? { key: 'source', group: 'navigate', glyph: 'collection', label: 'Open its collection', onClick: () => navigate(`/collections/${normalizeId(pkg.sourceCollectionId)}`) } : null,
		// A phone has no room for the labeled secondaries: they fold in here.
		...(phone ? secondaries : []),
		pkg.status !== 'retired' ? { key: 'retire', group: 'destructive', glyph: 'archive', label: 'Retire…', disabled: busy === 'retire', onClick: retire } : null,
		// Last: only a package never listed (the API's `canDelete`); one ever listed is only retired.
		pkg.canDelete ? { key: 'delete', group: 'destructive', glyph: 'delete', label: 'Delete draft…', disabled: busy === 'delete', onClick: deleteDraft } : null,
	].filter(Boolean) : null;

	const actions = pkg ? (
		<>
			{phone ? null : secondaries.map(s => <Button key={s.key} onClick={s.onClick} disabled={s.disabled} loading={busy === s.key}>{s.label}</Button>)}
			{!listed ? <Button type="primary" onClick={publish} loading={busy === 'publish'}>Publish</Button> : null}
		</>
	) : null;

	const left = buildReportLines(pkg?.report);
	const requirementLines = requirementReportLines(pkg?.report, pkg?.cards);
	// Its keywords and Good for lines (keepr-api KPR-1431); the detail's `tags` are the tags it installs.
	const keywords = pkg ? keywordsOf(pkg) : [];
	const goodFor = Array.isArray(pkg?.goodFor) ? pkg.goodFor : [];
	const cards = Array.isArray(pkg?.cards) ? pkg.cards : [];

	return (
		<div className="publishing-package">
			<ContextBar
				breadcrumb={[PUBLISHING_CRUMB]}
				kind={pkg?.kind === 'collection' ? 'collection' : 'card'}
				{...(pkg ? packageIdentity(pkg) : { record: null })}
				title={pkg?.name || 'Package'}
				below={pkg ? (
					<>
						<Tag className="publishing-package__status">{STATUS_LABELS[pkg.status] || pkg.status}</Tag>
						{pkg.waiting ? <Tag className="publishing-package__status">Waiting for review</Tag> : null}
						{packageMetaLine(pkg, PACKAGE_KIND_LABELS)}
					</>
				) : null}
				actions={actions}
				menuActions={menuActions}
			/>
			{missing ? (
				<div className="list-empty"><span>{missing} <Link to="/publishing">Back to Publishing</Link></span></div>
			) : (
				<DataState
					state={deriveDataState({ loading, error: pkg ? null : error, hasData: Boolean(pkg) })}
					hasData={Boolean(pkg)}
					skeleton={<Skeleton variant="list-row" count={5} />}
					error={error ? 'Check your connection and try again.' : null}
					onRetry={load}
					label="this package"
				>
					{pkg ? (
						<div className="publishing-package__body">
							{notice ? (
								<Alert
									type={notice.type}
									showIcon
									closable
									onClose={() => setNotice(null)}
									message={notice.text}
									className="publishing-package__notice"
									role={notice.type === 'error' ? 'alert' : 'status'}
								/>
							) : null}
							<p className="publishing-package__state">{statusNote(pkg)}</p>
							{pkg.waiting ? <p className="publishing-package__waiting" role="status">{waitingWords(pkg.waiting)}</p> : null}

							<section className="publishing-package__section" aria-labelledby="publishing-listing">
								<h2 id="publishing-listing" className="publishing-package__heading">Listing</h2>
								{pkg.description
									? <MarkdownContent markdown={pkg.description} className="publishing-package__description" />
									: <p className="publishing-package__quiet">No description yet. Edit the listing to say what it is for.</p>}
								<dl className="publishing-package__facts">
									<dt>Category</dt>
									<dd>{pkg.category || <span className="publishing-package__quiet">None yet: publishing needs one</span>}</dd>
									<dt>Keywords</dt>
									<dd>{keywords.length ? keywords.map(word => <Tag key={word}>{word}</Tag>) : <span className="publishing-package__quiet">None</span>}</dd>
									<dt>Good for</dt>
									<dd>{goodFor.length ? <ul className="publishing-package__good-for">{goodFor.map(line => <li key={line}>{line}</li>)}</ul> : <span className="publishing-package__quiet">None</span>}</dd>
									<dt>Address</dt>
									<dd>{listed ? <Link to={packagePath(pkg.key)}>{packagePath(pkg.key)}</Link> : packagePath(pkg.key)}</dd>
								</dl>
							</section>

							<section className="publishing-package__section" aria-labelledby="publishing-inside">
								<h2 id="publishing-inside" className="publishing-package__heading">What&apos;s inside</h2>
								<p>{packageContentsLine(pkg) || 'Nothing yet.'}</p>
								{cards.length ? (
									<ul className="publishing-package__cards">
										{cards.map(card => (
											<li key={card.localId || card.key}>
												<RecordBadge kind="card" record={{ icon: card.icon, color: card.color, name: card.name }} title={card.name} size="sm" />
											</li>
										))}
									</ul>
								) : null}
								{!pkg.canRebuild ? (
									<p className="publishing-package__quiet">Only someone who manages the collection it was built from can rebuild it.</p>
								) : null}
								{requirementLines.length ? (
									<div className="publishing-package__left">
										<p>Cards that can use a card you have, as last built:</p>
										<ul>{requirementLines.map((line, i) => <li key={`requirement-${i}`}>{line}</li>)}</ul>
									</div>
								) : null}
								{left.length ? (
									<div className="publishing-package__left">
										<p>The last build left out:</p>
										<ul>{left.map((line, i) => <li key={`left-${i}`}>{line}</li>)}</ul>
									</div>
								) : null}
							</section>

							<PackageVersions
								packageId={pkg._id}
								refreshKey={reads}
								canRebuild={Boolean(pkg.canRebuild)}
								onEditListing={() => setEditing(true)}
								onRebuild={rebuild}
								onChanged={load}
							/>
						</div>
					) : null}
				</DataState>
			)}
			<EditListingModal pkg={editing ? pkg : null} onClose={() => setEditing(false)} onSaved={onListingSaved} />
			<RebuildPackageModal pkg={pkg} open={rebuilding} onClose={() => setRebuilding(false)} onChanged={load} onDone={onRebuilt} />
		</div>
	);
}
